import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Ticket } from "lucide-react";
import axios from "axios";
import { API_BASE } from "@/lib/supabase";
import EventCard from "@/components/site/EventCard";

const heroImage =
  "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMGZlc3RpdmFsJTIwY3Jvd2R8ZW58MHx8fHwxNzg3MDAyNDUzfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/events/featured`, { params: { limit: 6 } });
        if (!ignore) setEvents(data.items || []);
      } catch (e) {
        if (!ignore) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-svj-bg/60 via-svj-bg/85 to-svj-bg" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-svj-border bg-svj-card/60 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-widest text-slate-300">
              <Sparkles className="h-3.5 w-3.5 text-svj-cyan" />
              Lokale events, echte tickets
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">
              Tickets voor{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple">
                festivals & buurtevents
              </span>{" "}
              in de buurt.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              StudioVoorJou verkoopt tickets voor organisatoren in Nederland. Snel scheuren,
              scannen en binnen. Betaal veilig via iDEAL en andere methoden.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/events"
                data-testid="hero-cta-events"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-3 px-6 hover:shadow-[0_0_24px_rgba(214,61,122,0.5)] transition-all"
              >
                <Ticket className="h-5 w-5" />
                Bekijk alle events
              </Link>
              <a
                href="#featured"
                data-testid="hero-cta-featured"
                className="inline-flex items-center gap-2 rounded-full bg-transparent border-2 border-svj-cyan text-svj-cyan font-bold py-3 px-6 hover:bg-svj-cyan/10 transition-colors"
              >
                Uitgelicht
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured events */}
      <section id="featured" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Uitgelicht</div>
            <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Wat er nu speelt
            </h2>
          </div>
          <Link
            to="/events"
            data-testid="featured-view-all"
            className="text-sm font-semibold text-svj-cyan hover:text-white inline-flex items-center gap-1.5"
          >
            Alles bekijken <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10">
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-svj-card border border-svj-border rounded-2xl h-72" />
              ))}
            </div>
          )}
          {!loading && error && (
            <div
              data-testid="featured-error"
              className="rounded-2xl border border-svj-border bg-svj-card p-8 text-slate-300"
            >
              <div className="text-white font-semibold mb-2">Even niet bereikbaar</div>
              <p className="text-sm text-slate-400">
                We konden de events nu niet ophalen. Probeer de pagina te vernieuwen — meestal is het
                zo weer voorbij.
              </p>
              <p className="mt-2 text-[11px] text-slate-500">
                Technische melding: <span className="text-slate-400">{String(error)}</span>
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                data-testid="featured-retry"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2 px-5 text-sm hover:shadow-[0_0_20px_rgba(214,61,122,0.5)]"
              >
                Opnieuw laden
              </button>
            </div>
          )}
          {!loading && !error && events.length === 0 && (
            <div
              data-testid="featured-empty"
              className="rounded-2xl border border-svj-border bg-svj-card p-10 text-center"
            >
              <Ticket className="h-8 w-8 text-svj-cyan mx-auto" />
              <div className="mt-3 text-white font-semibold">Nog geen gepubliceerde events</div>
              <p className="mt-1 text-sm text-slate-400">
                Zodra er events zijn gepubliceerd verschijnen ze hier.
              </p>
            </div>
          )}
          {!loading && !error && events.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {events.map((ev) => (
                <EventCard key={ev.id} ev={ev} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-svj-border bg-svj-card/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { k: "Veilig betalen", v: "iDEAL & meer, versleuteld" },
            { k: "QR-tickets", v: "Direct in je inbox" },
            { k: "PDF-download", v: "Print of scan aan de deur" },
            { k: "Lokale focus", v: "Events uit jouw regio" },
          ].map((f) => (
            <div key={f.k}>
              <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">{f.k}</div>
              <div className="mt-1 text-sm text-white/90">{f.v}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
